<template>
  <div>
    <component :is="headerComponent" :mainHeader="true"></component>
    <div class="app-content">
      <div class="bd-layout" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
    <aside class="bd-sidebar">
      <div class="bd-sidebar-header bd-fade-in bd-card-lift">
        <h6 class="mb-0">Business Development</h6>
        <small class="text-muted">Tenders & Opportunities</small>
      </div>
      <nav class="bd-nav">
        <RouterLink v-if="canAccessOpportunities" to="/admin/bid/analytics" class="bd-nav-link" active-class="bd-nav-link-active">
          Dashboard
        </RouterLink>
        <div v-if="canAccessOpportunities || canAccessBidPrep || canAccessContracts" class="bd-nav-group">
          <button type="button" class="bd-nav-link bd-nav-link-parent bd-btn-scale" @click="toggleOpportunitiesMenu">
            <span class="flex-1">Opportunities</span>
            <i :class="['fas', showOpportunitiesSubmenu ? 'fa-chevron-up' : 'fa-chevron-down', 'submenu-chevron']"></i>
          </button>
          <transition name="slide-fade">
            <div v-if="showOpportunitiesSubmenu" class="bd-subnav">
              <RouterLink v-if="canAccessOpportunities" to="/admin/bid/opportunities" class="bd-nav-link bd-nav-link-sub" active-class="bd-nav-link-active">Opportunities</RouterLink>
              <RouterLink v-if="canAccessOpportunities" to="/admin/bid/opportunities/new" class="bd-nav-link bd-nav-link-sub" active-class="bd-nav-link-active">New Opportunity</RouterLink>
              <RouterLink v-if="canAccessContracts" to="/admin/bid/contracts" class="bd-nav-link bd-nav-link-sub" active-class="bd-nav-link-active">Contracts</RouterLink>
              <RouterLink v-if="canAccessOpportunities" to="/admin/loss-records" class="bd-nav-link bd-nav-link-sub" active-class="bd-nav-link-active">Loss Records</RouterLink>
              <RouterLink v-if="canAccessBidPrep" to="/admin/bid/checklists" class="bd-nav-link bd-nav-link-sub" active-class="bd-nav-link-active">Checklists</RouterLink>
            </div>
          </transition>
        </div>
        <RouterLink v-if="canAccessClients" to="/admin/bid/clients" class="bd-nav-link" active-class="bd-nav-link-active">Clients</RouterLink>
        <div class="bd-nav-section-label">Settings</div>
        <RouterLink to="/settings/categories" class="bd-nav-link" active-class="bd-nav-link-active">Configurations</RouterLink>
        <RouterLink v-if="canAccessOpportunities" to="/settings/compliance" class="bd-nav-link" active-class="bd-nav-link-active">Compliance Documents</RouterLink>
      </nav>
    </aside>
    <!-- floating toggle visible when sidebar is collapsed so user can re-open it -->
    <button
      v-if="sidebarCollapsed"
      class="bd-floating-toggle"
      @click="toggleSidebar"
      title="Open sidebar"
    >
      <i class="fas fa-chevron-right"></i>
    </button>
    <main class="bd-main">
      <div class="bd-topbar d-flex justify-content-between mb-2">
        <div class="d-flex align-items-center">
          <button
            type="button"
            class="btn btn-sm btn-light bd-toggle-btn"
            @click="toggleSidebar"
            :aria-expanded="!sidebarCollapsed"
            title="Toggle sidebar"
          >
            <i :class="['fas', sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left']"></i>
          </button>
        </div>
        <div class="d-flex align-items-center justify-content-end">
          <slot name="topbar" />
        </div>
      </div>
      <div v-if="$slots.default">
        <slot />
      </div>
      <router-view v-else v-slot="{ Component }">
        <Transition name="page" mode="out-in">
          <component :is="Component" :key="$route.path" />
        </Transition>
      </router-view>
      </main>
    </div>
  </div>
  </div>
</template>

<script>
export default {
  name: 'BDLayout',
  components: {
    headerComponent: require('./Header.vue').default,
  },
  mounted() {
    const adjust = () => {
      try {
        const hdr = document.getElementById('header')
        const app = document.querySelector('.app-content')
        if (hdr && app) {
          const h = Math.ceil(hdr.getBoundingClientRect().height) || 0
          document.documentElement.style.setProperty('--app-header-height', h + 'px')
          app.style.paddingTop = h + 'px'
        }
      } catch (e) {
        // ignore
      }
    }
    this.__bdLayoutResizeHandler = () => adjust()
    adjust()
    window.addEventListener('resize', this.__bdLayoutResizeHandler)
  },
  beforeUnmount() {
    if (this.__bdLayoutResizeHandler) window.removeEventListener('resize', this.__bdLayoutResizeHandler)
  },
  data() {
    return {
      showOpportunitiesSubmenu: true,
      sidebarCollapsed: JSON.parse(localStorage.getItem('bd_sidebar_collapsed') || 'false'),
    }
  },
  computed: {
    permission() {
      try {
        const p = localStorage.getItem('permission')
        return p ? JSON.parse(p) : {}
      } catch (e) {
        return {}
      }
    },
    canAccessOpportunities() { return this.permission?.approveAll || this.permission?.bdOpportunitiesManage },
    canAccessBidPrep() { return this.permission?.approveAll || this.permission?.bdBidPreparation },
    canAccessContracts() { return this.permission?.approveAll || this.permission?.bdContractHandover },
    canAccessClients() { return this.permission?.approveAll || this.permission?.bdOpportunitiesManage },
  },
  methods: {
    toggleOpportunitiesMenu() {
      this.showOpportunitiesSubmenu = !this.showOpportunitiesSubmenu
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed
      try { localStorage.setItem('bd_sidebar_collapsed', JSON.stringify(this.sidebarCollapsed)) } catch (e) { /* ignore */ }
    },
    // ensure sidebar is visible on larger screens (recover from accidental collapse)
    ensureSidebarVisibleOnLargeScreen() {
      try {
        const persisted = localStorage.getItem('bd_sidebar_collapsed')
        const collapsed = persisted ? JSON.parse(persisted) : false
        if (window.innerWidth >= 992 && collapsed) {
          // automatically expand on large screens
          this.sidebarCollapsed = false
          try { localStorage.setItem('bd_sidebar_collapsed', JSON.stringify(false)) } catch (e) { /* ignore */ }
        }
      } catch (e) {
        // ignore
      }
    },
  },
}
</script>

<style scoped>
.bd-layout {
  display: flex;
  /* use CSS var for header height so layout scales correctly when zoomed */
  min-height: calc(100vh - var(--app-header-height, 60px));
}

.bd-sidebar {
  width: 240px;
  background: #ffffff;
  color: #111827;
  padding: 1rem;
  border-right: 1px solid #e5e7eb;
}

.bd-sidebar-header {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
  margin-bottom: 0.75rem;
}

.bd-nav {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.bd-nav-group {
  display: flex;
  flex-direction: column;
}

.bd-nav-section-label {
  margin-top: 1rem;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
}

.bd-nav-link {
  display: flex;
  align-items: center;
  padding: 0.45rem 0.6rem;
  border-radius: 0.25rem;
  color: #374151;
  text-decoration: none;
  font-size: 0.9rem;
}

.bd-nav-link-sub {
  padding-left: 1.75rem;
  font-size: 0.85rem;
}

.bd-nav-link-parent {
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;
  cursor: pointer;
}

.submenu-chevron {
  margin-left: auto;
  font-size: 0.7rem;
}

.bd-nav-link:hover {
  background: #eff6ff;
  color: #1d4ed8;
}

.bd-nav-link-active {
  background: #2563eb;
  color: #ffffff;
}

.bd-main {
  flex: 1;
  background: #f5f7fb;
  padding: 0.75rem 1rem 1.25rem;
}

/* Collapsed sidebar styles */
.sidebar-collapsed .bd-sidebar {
  width: 0 !important;
  padding: 0 !important;
  overflow: hidden;
}
.sidebar-collapsed .bd-sidebar .bd-sidebar-header,
.sidebar-collapsed .bd-sidebar .bd-nav { display: none !important; }
.bd-toggle-btn {
  border-radius: 6px;
  padding: 6px 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6; /* subtle grey background */
  border: 1px solid #e5e7eb;
  color: #374151;
}
.bd-toggle-btn:hover {
  background: #e9eef7;
  border-color: #d1d5db;
}
.bd-toggle-btn i { width: 13px; text-align: center }

@media (max-width: 768px) {
  .bd-layout {
    flex-direction: column;
  }

  .bd-sidebar {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

/* floating toggle when sidebar is hidden */
.bd-floating-toggle {
  position: fixed;
  left: 8px;
  top: calc(var(--app-header-height, 60px) + 8px);
  z-index: 1100;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid rgba(0,0,0,0.08);
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 11px rgba(16,24,40,0.06);
}
.bd-floating-toggle i { color: #374151 }

@media (max-width: 992px) {
  .bd-floating-toggle { left: 6px; top: calc(var(--app-header-height, 60px) + 6px); }
}
</style>
